<template>
  <div class="flex flex-col items-center justify-center w-full pb-20">
    <section class="flex flex-col items-center w-full">
      <!-- Header with Summary Stats -->
      <div class="w-full mb-6 bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-lg font-bold mb-4">Profile Update History</h2>

        <div
          v-if="isLoading && updateChanges.length === 0"
          class="py-4 text-center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            class="mb-2"
          ></v-progress-circular>
          <p class="text-gray-500">Loading update history...</p>
        </div>

        <div
          v-else-if="hasError"
          class="bg-red-50 p-4 rounded-lg border border-red-200 text-center"
        >
          <v-icon color="error" class="mb-2">mdi-alert-circle</v-icon>
          <p class="text-red-700 font-medium">Failed to load update history</p>
          <p class="text-red-600 text-sm">{{ errorMessage }}</p>
          <v-btn
            small
            color="error"
            class="mt-2"
            @click="initializeDataLoading"
          >
            <v-icon small left>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </div>

        <div
          v-else-if="updateChanges.length > 0"
          class="grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          <div
            class="stat-card bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200"
          >
            <div class="flex items-center mb-2">
              <v-icon color="primary" class="mr-2">mdi-history</v-icon>
              <h3 class="font-semibold text-gray-700">Total Updates</h3>
            </div>
            <div class="text-2xl font-bold text-primary">
              {{ updateChanges.length }}
            </div>
            <div class="text-sm text-gray-500">All time</div>
          </div>

          <div
            class="stat-card bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200"
          >
            <div class="flex items-center mb-2">
              <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
              <h3 class="font-semibold text-gray-700">Success Rate</h3>
            </div>
            <div class="text-2xl font-bold text-green-600">
              {{ calculateSuccessRate }}%
            </div>
            <div class="text-sm text-gray-500">Across all updates</div>
          </div>

          <div
            class="stat-card bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200"
          >
            <div class="flex items-center mb-2">
              <v-icon color="#8050DE" class="mr-2">mdi-tag-multiple</v-icon>
              <h3 class="font-semibold text-gray-700">Most Common Update</h3>
            </div>
            <div class="text-lg font-medium text-purple-700">
              {{ mostCommonUpdateType }}
            </div>
            <div class="text-sm text-gray-500">
              {{ mostCommonUpdateCount }} updates
            </div>
          </div>
        </div>
      </div>

      <!-- Filter Controls -->
      <div
        v-if="updateChanges.length > 0"
        class="w-full mb-6 bg-white rounded-lg shadow-sm p-6"
      >
        <div class="flex flex-wrap gap-4 justify-between items-center">
          <div class="flex items-center space-x-2">
            <v-text-field
              v-model="searchQuery"
              placeholder="Search updates..."
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              hide-details
              class="max-w-xs"
            ></v-text-field>
          </div>

          <div class="flex flex-wrap gap-2">
            <!-- Type filters -->
            <v-chip
              v-for="type in updateTypes"
              :key="type.value"
              :color="selectedTypes.includes(type.value) ? type.color : ''"
              outlined
              @click="toggleTypeFilter(type.value)"
              class="cursor-pointer filter-chip"
              :class="{ 'selected-chip': selectedTypes.includes(type.value) }"
            >
              <span class="filter-icon-wrapper mr-1">
                <i
                  v-if="type.value === 'single'"
                  class="mdi mdi-map-marker"
                ></i>
                <i
                  v-else-if="type.value === 'recommended'"
                  class="mdi mdi-lightbulb-on"
                ></i>
                <i v-else class="mdi mdi-file-multiple"></i>
              </span>
              {{ type.label }}
            </v-chip>

            <!-- Status filters -->
            <v-chip
              v-for="status in statusTypes"
              :key="status.value"
              :color="
                selectedStatuses.includes(status.value) ? status.color : ''
              "
              outlined
              @click="toggleStatusFilter(status.value)"
              class="cursor-pointer filter-chip"
              :class="{
                'selected-chip': selectedStatuses.includes(status.value),
              }"
            >
              <span class="filter-icon-wrapper mr-1">
                <i
                  :class="{
                    'mdi mdi-check-circle': status.value === 'verified',
                    'mdi mdi-close-circle': status.value === 'rejected',
                    'mdi mdi-clock': status.value === 'queued',
                  }"
                ></i>
              </span>
              {{ status.label }}
            </v-chip>

            <v-btn
              small
              text
              icon
              color="gray"
              @click="resetFilters"
              title="Reset filters"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="!isLoading && updateChanges.length === 0 && !hasError"
        class="w-full"
      >
        <div
          class="bg-amber-50 p-8 rounded-lg border border-amber-200 flex flex-col items-center justify-center mb-6"
        >
          <v-icon color="amber darken-2" size="48" class="mb-4"
            >mdi-history</v-icon
          >
          <p class="text-amber-800 font-medium text-center text-lg mb-2">
            No update history available
          </p>
          <p class="text-amber-700 text-center max-w-md">
            When you make changes to your Google Business Profiles, all update
            requests and their status will appear here.
          </p>
        </div>
      </div>

      <!-- History Timeline -->
      <div v-else-if="updateChanges.length > 0" class="w-full">
        <div
          v-for="(entry, index) in filteredUpdateChanges"
          :key="entry.id"
          class="mb-6 relative"
        >
          <!-- Timeline connector -->
          <div
            v-if="index < filteredUpdateChanges.length - 1"
            class="absolute left-6 top-16 bottom-0 w-0.5 bg-gray-200 z-0"
          ></div>

          <!-- Entry Card -->
          <div class="bg-white rounded-lg shadow-sm p-6 relative z-10">
            <div class="flex items-start">
              <!-- Timeline dot with fixed dimensions and explicit icon container -->
              <div
                class="timeline-icon-container mr-4 flex-shrink-0"
                :class="{
                  'bg-blue-100': entry.type === 'single',
                  'bg-purple-100':
                    entry.type === 'multiple' || entry.type === 'bulk',
                  'bg-green-100': entry.type === 'recommended',
                }"
              >
                <span
                  class="icon-wrapper"
                  :class="{
                    'text-blue-600': entry.type === 'single',
                    'text-purple-600':
                      entry.type === 'multiple' || entry.type === 'bulk',
                    'text-green-600': entry.type === 'recommended',
                  }"
                >
                  <i
                    v-if="entry.type === 'single'"
                    class="mdi mdi-map-marker"
                  ></i>
                  <i
                    v-else-if="entry.type === 'recommended'"
                    class="mdi mdi-lightbulb-on"
                  ></i>
                  <i v-else class="mdi mdi-file-multiple"></i>
                </span>
              </div>

              <!-- Content -->
              <div class="flex-1">
                <div class="flex flex-wrap justify-between items-start mb-2">
                  <div>
                    <div class="flex flex-wrap gap-2 items-center">
                      <h3 class="font-semibold text-lg">{{ entry.title }}</h3>
                      <span
                        class="text-xs px-2 py-1 rounded-full"
                        :class="{
                          'bg-blue-100 text-blue-800': entry.type === 'single',
                          'bg-[#f3e8ff] text-[#5b21b6]':
                            entry.type === 'multiple' || entry.type === 'bulk',
                          'bg-green-100 text-green-800':
                            entry.type === 'recommended',
                        }"
                      >
                        {{
                          entry.type === "single"
                            ? "Single Location"
                            : entry.type === "recommended"
                            ? "Recommendation"
                            : "Bulk Update"
                        }}
                      </span>
                    </div>
                    <p class="text-gray-600">
                      {{ entry.address || "Multiple locations" }}
                    </p>
                  </div>

                  <div class="text-right">
                    <p class="text-sm text-gray-500 whitespace-nowrap">
                      {{ formatFirestoreDate(entry.requested_at) }}
                    </p>
                    <p class="text-sm text-gray-600">
                      by {{ entry.requested_by || "Unknown" }}
                    </p>
                  </div>
                </div>

                <!-- Update Stats -->
                <div class="flex flex-wrap gap-3 mt-3 items-center">
                  <!-- Progress bar container with fixed width -->
                  <div class="w-[300px]">
                    <div
                      class="h-2 bg-gray-200 rounded-full overflow-hidden flex"
                    >
                      <div
                        class="h-full bg-green-500"
                        :style="`width: ${getStatusPercentage(
                          entry,
                          'success'
                        )}%`"
                      ></div>
                      <div
                        class="h-full bg-orange-500"
                        :style="`width: ${getStatusPercentage(
                          entry,
                          'queued'
                        )}%`"
                      ></div>
                      <div
                        class="h-full bg-blue-500"
                        :style="`width: ${getStatusPercentage(
                          entry,
                          'verified'
                        )}%`"
                      ></div>
                      <div
                        class="h-full bg-pink-500"
                        :style="`width: ${getStatusPercentage(
                          entry,
                          'rejected'
                        )}%`"
                      ></div>
                      <div
                        class="h-full bg-red-500"
                        :style="`width: ${getStatusPercentage(
                          entry,
                          'failed'
                        )}%`"
                      ></div>
                    </div>
                    <div class="grid grid-cols-3 gap-2 text-xs mt-2">
                      <div
                        v-if="getStatusCount(entry, 'success') > 0"
                        class="text-green-600"
                      >
                        {{ getStatusCount(entry, "success") }} successful
                      </div>
                      <div
                        v-if="getStatusCount(entry, 'queued') > 0"
                        class="text-orange-600"
                      >
                        {{ getStatusCount(entry, "queued") }} queued
                      </div>
                      <div
                        v-if="getStatusCount(entry, 'verified') > 0"
                        class="text-blue-600"
                      >
                        {{ getStatusCount(entry, "verified") }} verified
                      </div>
                      <div
                        v-if="getStatusCount(entry, 'rejected') > 0"
                        class="text-pink-600"
                      >
                        {{ getStatusCount(entry, "rejected") }} rejected
                      </div>
                      <div
                        v-if="getStatusCount(entry, 'failed') > 0"
                        class="text-red-600"
                      >
                        {{ getStatusCount(entry, "failed") }} failed
                      </div>
                    </div>
                  </div>

                  <!-- Buttons and Status Chips container -->
                  <div class="flex flex-wrap gap-2 items-center">
                    <v-btn
                      small
                      :color="entry.showChanges ? 'gray' : 'primary'"
                      :outlined="!entry.showChanges"
                      @click="toggleDetails(entry)"
                    >
                      <v-icon small left>{{
                        entry.showChanges
                          ? "mdi-chevron-up"
                          : "mdi-information-outline"
                      }}</v-icon>
                      {{ entry.showChanges ? "Hide" : "Details" }}
                    </v-btn>

                    <v-btn
                      small
                      color="primary"
                      outlined
                      @click="showUpdateResultsDialog(entry)"
                    >
                      <v-icon small left>mdi-eye</v-icon>
                      Results
                    </v-btn>

                    <!-- Status Chips (only if count > 0) -->
                    <template v-for="status in statusList">
                      <v-chip
                        v-if="getStatusCount(entry, status.value) > 0"
                        :key="status.value"
                        :color="getStatusColor(status.value)"
                        text-color="white"
                        small
                        class="font-semibold"
                      >
                        {{ getStatusCount(entry, status.value) }}
                        {{ status.label }}
                      </v-chip>
                    </template>
                  </div>
                </div>

                <!-- Add bulk locations summary -->
                <div
                  v-if="
                    (entry.type === 'multiple' || entry.type === 'bulk') &&
                    entry.showChanges &&
                    isMultiLocation(entry)
                  "
                  class="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div class="flex justify-between items-center mb-2">
                    <h4 class="font-medium text-gray-700 flex items-center">
                      <span class="filter-icon-wrapper mr-1">
                        <i class="mdi mdi-map-marker-multiple"></i>
                      </span>
                      Affected Locations ({{
                        getAffectedLocationsCount(entry)
                      }})
                    </h4>
                    <v-btn
                      x-small
                      text
                      color="primary"
                      @click="toggleLocationsList(entry)"
                    >
                      {{ entry.showAllLocations ? "Show Less" : "Show All" }}
                    </v-btn>
                  </div>

                  <div class="locations-list mt-2">
                    <div
                      v-for="(location, index) in getVisibleLocations(entry)"
                      :key="index"
                      class="px-3 py-2 bg-white rounded mb-1 flex items-center"
                    >
                      <span class="filter-icon-wrapper mr-2 text-blue-600">
                        <i class="mdi mdi-map-marker"></i>
                      </span>
                      <span class="font-medium text-sm">{{
                        getLocationName(location)
                      }}</span>
                      <span
                        v-if="location.address"
                        class="text-xs text-gray-500 ml-2"
                        >{{ location.address }}</span
                      >
                    </div>

                    <div
                      v-if="
                        !entry.showAllLocations &&
                        getAffectedLocationsCount(entry) > 3
                      "
                      class="text-center text-sm text-gray-500 mt-2"
                    >
                      {{ getAffectedLocationsCount(entry) - 3 }} more locations
                    </div>
                  </div>
                </div>

                <!-- Details (expanded) -->
                <transition name="fade">
                  <div
                    v-if="entry.showChanges"
                    class="mt-6 pt-4 border-t border-gray-200"
                  >
                    <!-- For recommendation type -->
                    <template
                      v-if="
                        entry.type === 'recommended' && entry.recommendation
                      "
                    >
                      <div class="bg-gray-50 p-4 rounded-lg mb-4 border">
                        <div class="mb-4">
                          <h4 class="font-medium text-gray-700 mb-2">Field</h4>
                          <p class="text-gray-600 ml-4">
                            {{ formatFieldName(entry.recommendation.field) }}
                          </p>
                        </div>

                        <div
                          v-if="entry.recommendation.consistency_analysis"
                          class="mb-4"
                        >
                          <h4 class="font-medium text-gray-700 mb-2">
                            Analysis
                          </h4>
                          <p class="text-gray-600 ml-4">
                            {{
                              entry.recommendation.consistency_analysis.reason
                            }}
                          </p>

                          <div
                            v-if="
                              entry.recommendation.consistency_analysis
                                .consistency_score !== undefined
                            "
                            class="ml-4 flex items-center mt-2"
                          >
                            <span class="text-gray-600 mr-2"
                              >Consistency Score:</span
                            >
                            <div class="w-24 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                class="bg-blue-600 h-2 rounded-full"
                                :style="`width: ${
                                  entry.recommendation.consistency_analysis
                                    .consistency_score * 100
                                }%`"
                              ></div>
                            </div>
                            <span
                              >{{
                                (
                                  entry.recommendation.consistency_analysis
                                    .consistency_score * 100
                                ).toFixed(0)
                              }}%</span
                            >
                          </div>

                          <div
                            v-if="
                              entry.recommendation.consistency_analysis
                                .overall_impact_score !== undefined
                            "
                            class="ml-4 flex items-center mt-2"
                          >
                            <span class="text-gray-600 mr-2"
                              >Impact Score:</span
                            >
                            <div class="w-24 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                class="bg-blue-600 h-2 rounded-full"
                                :style="`width: ${
                                  entry.recommendation.consistency_analysis
                                    .overall_impact_score * 100
                                }%`"
                              ></div>
                            </div>
                            <span
                              >{{
                                (
                                  entry.recommendation.consistency_analysis
                                    .overall_impact_score * 100
                                ).toFixed(0)
                              }}%</span
                            >
                          </div>
                        </div>

                        <div class="mb-4">
                          <div class="flex justify-between items-center">
                            <h4 class="font-medium text-gray-700">
                              Location Changes
                            </h4>
                            <div class="flex items-center gap-2">
                              <v-text-field
                                v-model="recommendationSearchQuery"
                                placeholder="Search locations..."
                                dense
                                outlined
                                hide-details
                                class="max-w-xs"
                                prepend-inner-icon="mdi-magnify"
                              ></v-text-field>
                            </div>
                          </div>

                          <v-data-table
                            :headers="[
                              { text: 'Location', value: 'name' },
                              { text: 'Old Value', value: 'current_value' },
                              { text: 'New Value', value: 'recommended_value' },
                            ]"
                            :items="
                              filteredLocationRecommendations(
                                entry.recommendation
                              )
                            "
                            :items-per-page="5"
                            :footer-props="{
                              'items-per-page-options': [5, 10, 25, 50],
                            }"
                            :search="recommendationSearchQuery"
                            class="elevation-0 mt-3"
                            dense
                          >
                            <template v-slot:item.current_value="{ item }">
                              <span class="text-gray-600">{{
                                item.current_value || "Missing"
                              }}</span>
                            </template>
                            <template v-slot:item.recommended_value="{ item }">
                              <span class="text-green-600">{{
                                item.recommended_values.recommended_value
                              }}</span>
                            </template>
                          </v-data-table>
                        </div>
                      </div>
                    </template>

                    <!-- For other types -->
                    <div
                      v-for="(change, index) in entry.changes"
                      :key="index"
                      class="border p-4 rounded-lg bg-gray-50 mb-3"
                    >
                      <h4
                        class="font-medium text-gray-700 mb-2 flex items-center"
                      >
                        <v-icon small color="primary" class="mr-2"
                          >mdi-pencil</v-icon
                        >
                        {{ change.field }}
                      </h4>

                      <template>
                        <!-- For multiple-type items -->
                        <template
                          v-if="
                            entry.type === 'multiple' &&
                            Array.isArray(change.new)
                          "
                        >
                          <div
                            v-for="(subChange, subIndex) in showAllSubChanges
                              ? change.new
                              : change.new.slice(0, 5)"
                            :key="subIndex"
                            class="ml-4 mt-2 p-2 border-l-4 border-blue-200 bg-white rounded-r-md"
                          >
                            <p class="text-sm font-medium">
                              {{ subChange.displayName }}
                            </p>
                            <div class="flex flex-wrap gap-4 mt-1">
                              <div>
                                <p class="text-xs text-gray-500">Old:</p>
                                <p class="text-sm text-gray-600">
                                  {{ subChange.oldValue || "None" }}
                                </p>
                              </div>
                              <div>
                                <p class="text-xs text-gray-500">New:</p>
                                <p class="text-sm text-green-600">
                                  {{ subChange.newValue || "None" }}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="!showAllSubChanges && change.new.length > 5"
                            class="ml-4 mt-2"
                          >
                            <v-btn
                              small
                              text
                              color="primary"
                              @click="showAllSubChanges = true"
                            >
                              <v-icon small left>mdi-eye</v-icon>
                              Show {{ change.new.length - 5 }} more items
                            </v-btn>
                          </div>
                        </template>

                        <!-- For single-type subChanges -->
                        <template v-else-if="Array.isArray(change.changes)">
                          <div
                            v-for="(subChange, subIndex) in showAllSubChanges
                              ? change.changes
                              : change.changes.slice(0, 5)"
                            :key="subIndex"
                            class="ml-4 mt-2 p-2 border-l-4 border-blue-200 bg-white rounded-r-md"
                          >
                            <p class="text-sm font-medium">
                              {{ subChange.displayName }}
                            </p>
                            <div class="flex flex-wrap gap-4 mt-1">
                              <div>
                                <p class="text-xs text-gray-500">Old:</p>
                                <p class="text-sm text-gray-600">
                                  {{ subChange.oldValue || "None" }}
                                </p>
                              </div>
                              <div>
                                <p class="text-xs text-gray-500">New:</p>
                                <p class="text-sm text-green-600">
                                  {{ subChange.newValue || "None" }}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="
                              !showAllSubChanges && change.changes.length > 5
                            "
                            class="ml-4 mt-2"
                          >
                            <v-btn
                              small
                              text
                              color="primary"
                              @click="showAllSubChanges = true"
                            >
                              <v-icon small left>mdi-eye</v-icon>
                              Show {{ change.changes.length - 5 }} more items
                            </v-btn>
                          </div>
                        </template>

                        <!-- --------------------------------------- -->
                        <!-- SINGLE or MULTIPLE value presentation   -->
                        <!-- --------------------------------------- -->
                        <template v-else>
                          <!-- ───────────── ① SINGLE LOCATION ───────────── -->
                          <div
                            v-if="entry.type === 'single'"
                            class="grid grid-cols-1 md:grid-cols-2 gap-4 ml-4"
                          >
                            <!-- ▸ OLD value ▸──────────────────────────────-->
                            <div class="p-3 bg-white rounded-md">
                              <p class="text-xs text-gray-500 font-medium mb-1">
                                Old Value:
                              </p>

                              <!-- (a) image url  → thumbnail -->
                              <div v-if="isImageUrl(change.old)">
                                <v-img
                                  :src="thumbUrl(change.old)"
                                  max-width="120"
                                  aspect-ratio="1"
                                  contain
                                  class="rounded-lg elevation-1"
                                />
                                <a
                                  :href="change.old"
                                  target="_blank"
                                  class="text-xs text-blue-600 underline block mt-1"
                                  >open full image</a
                                >
                              </div>

                              <!-- (b) anything else → plain text -->
                              <p
                                v-else
                                class="text-sm text-gray-600 break-words whitespace-pre-line"
                              >
                                {{
                                  formatChangeValue(change.field, change.old)
                                }}
                              </p>
                            </div>

                            <!-- ▸ NEW value ▸──────────────────────────────-->
                            <div class="p-3 bg-white rounded-md">
                              <p class="text-xs text-gray-500 font-medium mb-1">
                                New Value:
                              </p>

                              <div v-if="isImageUrl(change.new)">
                                <v-img
                                  :src="thumbUrl(change.new)"
                                  max-width="120"
                                  aspect-ratio="1"
                                  contain
                                  class="rounded-lg elevation-1"
                                />
                                <a
                                  :href="change.new"
                                  target="_blank"
                                  class="text-xs text-blue-600 underline block mt-1"
                                  >open full image</a
                                >
                              </div>

                              <p
                                v-else
                                class="text-sm text-green-600 break-words whitespace-pre-line"
                              >
                                {{
                                  formatChangeValue(change.field, change.new)
                                }}
                              </p>
                            </div>
                          </div>

                          <!-- ───────────── ② BULK / MULTIPLE ───────────── -->
                          <div
                            v-else-if="entry.type === 'multiple'"
                            class="ml-4 p-3 bg-white rounded-md"
                          >
                            <p class="text-xs text-gray-500 font-medium mb-1">
                              Change:
                            </p>

                            <div v-if="isImageUrl(change.new)">
                              <v-img
                                :src="thumbUrl(change.new)"
                                max-width="120"
                                aspect-ratio="1"
                                contain
                                class="rounded-lg elevation-1"
                              />
                              <a
                                :href="change.new"
                                target="_blank"
                                class="text-xs text-blue-600 underline block mt-1"
                                >open full image</a
                              >
                            </div>

                            <p
                              v-else
                              class="text-sm text-green-600 break-words whitespace-pre-line"
                            >
                              {{ formatChangeValue(change.field, change.new) }}
                            </p>
                          </div>
                        </template>
                      </template>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <!-- Infinite Loading with improved spinner and error states -->
        <InfiniteLoading ref="loader" @infinite="fetchChangeHistory">
          <div slot="spinner" class="py-6">
            <v-progress-circular
              indeterminate
              color="primary"
              class="mx-auto d-block"
            ></v-progress-circular>
            <p class="text-center text-gray-500 mt-2">
              Loading more history...
            </p>
          </div>
          <div slot="no-more" class="py-4 text-center text-gray-500">
            All history loaded
          </div>
          <div slot="no-results" class="py-4 text-center text-gray-500">
            No updates available
          </div>
          <div slot="error" class="py-4 text-center text-red-500">
            <v-icon color="error" class="mb-2">mdi-alert-circle</v-icon>
            <p>Error loading more updates</p>
            <v-btn
              small
              color="error"
              outlined
              class="mt-2"
              @click="$refs.loader.stateChanger.reset()"
            >
              Retry
            </v-btn>
          </div>
        </InfiniteLoading>
      </div>
    </section>

    <!-- Update Results Dialog -->
    <v-dialog v-model="showUpdateResults" max-width="700px" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="flex items-center">
            <v-icon left color="primary">mdi-clipboard-check</v-icon>
            <span>Update Results</span>
          </div>
          <v-btn icon @click="showUpdateResults = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Summary Stats -->
          <div class="grid grid-cols-2 gap-4 mb-6">
            <div
              class="p-4 bg-green-50 border border-green-200 rounded-lg text-center"
            >
              <div class="text-sm text-gray-600">Successful</div>
              <div class="text-3xl font-bold text-green-600">
                {{ getSuccessCount(currentHistory) }}
              </div>
              <div class="text-xs text-gray-500">
                {{ Math.round(getSuccessPercentage(currentHistory)) }}% of
                updates
              </div>
            </div>
            <div
              class="p-4 bg-red-50 border border-red-200 rounded-lg text-center"
            >
              <div class="text-sm text-gray-600">Failed</div>
              <div class="text-3xl font-bold text-red-600">
                {{ getFailedCount(currentHistory) }}
              </div>
              <div class="text-xs text-gray-500">
                {{ Math.round(getFailedPercentage(currentHistory)) }}% of
                updates
              </div>
            </div>
          </div>

          <!-- Results Table -->
          <v-data-table
            :headers="[
              { text: 'Location', value: 'title' },
              { text: 'Field', value: 'field' },
              { text: 'Status', value: 'status' },
            ]"
            :items="currentUpdateResults || []"
            :items-per-page="10"
            :search="resultSearchQuery"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100],
            }"
            dense
          >
            <template v-slot:top>
              <v-text-field
                v-model="resultSearchQuery"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search results..."
                hide-details
                dense
                outlined
                class="mx-4 mt-4"
              ></v-text-field>
            </template>
            <template v-slot:item.field="{ item }">
              {{ formatFieldName(item.field) }}
            </template>
            <template v-slot:item.status="{ item }">
              <div class="flex items-center">
                <v-chip
                  small
                  :color="getStatusColor(item.status)"
                  text-color="white"
                >
                  {{ item.status }}
                </v-chip>

                <!-- Show info icon only when there's an error -->
                <v-tooltip v-if="item.status === 'failed' && item.error" bottom>
                  <template #activator="{ on }">
                    <v-icon
                      small
                      class="ml-1 cursor-pointer text-blue-500"
                      v-on="on"
                    >
                      mdi-information
                    </v-icon>
                  </template>
                  <span>{{ getErrorMessage(item.error) }}</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { db } from "@/utils/firebase.utils";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import InfiniteLoading from "vue-infinite-loading";
import connectedAppsMixins from "@/mixins/connected.apps.mixins";
import { GMB_ERROR_MESSAGES } from "@/utils/common.utils";

export default {
  props: {
    categories: { type: Array, required: true },
  },
  components: {
    InfiniteLoading,
  },
  mixins: [connectedAppsMixins],
  data() {
    return {
      recommendationSearchQuery: "",
      resultSearchQuery: "",
      searchQuery: "",
      selectedTypes: [],
      selectedStatuses: [],
      batchSize: 10,
      lastDoc: null,
      updateChanges: [],
      showAllSubChanges: false,
      showUpdateResults: false,
      currentUpdateResults: null,
      currentHistory: null,
      updateTypes: [
        {
          label: "Single",
          value: "single",
          color: "blue",
          icon: "mdi-map-marker",
        },
        {
          label: "Bulk",
          value: "bulk",
          color: "purple darken-2",
          icon: "mdi-file-multiple",
        },
        {
          label: "Recommended",
          value: "recommended",
          color: "green",
          icon: "mdi-lightbulb-on",
        },
      ],
      statusTypes: [
        { label: "Verified", value: "verified", color: "blue" },
        { label: "Rejected", value: "rejected", color: "pink" },
        { label: "Queued", value: "queued", color: "orange" },
      ],
      isLoading: true,
      hasError: false,
      errorMessage: "",
      locations: [],
      statusList: [
        { label: "Queued", value: "queued" },
        { label: "Verified", value: "verified" },
        { label: "Rejected", value: "rejected" },
        { label: "Success", value: "success" },
        { label: "Failed", value: "failed" },
      ],
    };
  },
  created() {
    // Initialize data loading
    this.initializeDataLoading();

    // Also fetch locations for location name display
    this.fetchLocations();
  },
  mounted() {
    // Log relevant values for debugging
    console.log("GMBProfileHistory mounted with:", {
      hasUID: !!this.currentUID,
      hasWID: !!this.WID,
      UIDPrefix: this.currentUID
        ? this.currentUID.substring(0, 4) + "..."
        : "null",
      WIDPrefix: this.WID ? this.WID.substring(0, 4) + "..." : "null",
    });
  },
  computed: {
    filteredUpdateChanges() {
      if (!this.updateChanges.length) return [];

      let filtered = [...this.updateChanges];

      // Filter by type if any types are selected
      if (this.selectedTypes.length > 0) {
        filtered = filtered.filter(
          (entry) =>
            this.selectedTypes.includes(entry.type) ||
            (entry.type === "multiple" && this.selectedTypes.includes("bulk"))
        );
      }

      // Filter by status if any statuses are selected
      if (this.selectedStatuses.length > 0) {
        filtered = filtered.filter((entry) =>
          this.selectedStatuses.some(
            (status) => this.getStatusCount(entry, status) > 0
          )
        );
      }

      // Filter by search query
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((entry) => {
          return (
            (entry.title && entry.title.toLowerCase().includes(query)) ||
            (entry.address && entry.address.toLowerCase().includes(query)) ||
            (entry.requested_by &&
              entry.requested_by.toLowerCase().includes(query)) ||
            (entry.type && entry.type.toLowerCase().includes(query))
          );
        });
      }

      return filtered;
    },

    calculateSuccessRate() {
      if (!this.updateChanges.length) return 0;

      let totalSuccessful = 0;
      let totalCount = 0;

      this.updateChanges.forEach((entry) => {
        const successCount = this.getSuccessCount(entry);
        const failedCount = this.getFailedCount(entry);
        totalSuccessful += successCount;
        totalCount += successCount + failedCount;
      });

      return totalCount ? Math.round((totalSuccessful / totalCount) * 100) : 0;
    },

    mostCommonUpdateType() {
      if (!this.updateChanges.length) return "None";

      const typeCounts = {
        single: 0,
        bulk: 0,
        recommended: 0,
      };

      this.updateChanges.forEach((entry) => {
        if (entry.type === "single") typeCounts.single++;
        else if (entry.type === "recommended") typeCounts.recommended++;
        else typeCounts.bulk++; // 'multiple' or 'bulk'
      });

      // Find the type with the most occurrences
      let maxType = "None";
      let maxCount = 0;

      Object.entries(typeCounts).forEach(([type, count]) => {
        if (count > maxCount) {
          maxType = type;
          maxCount = count;
        }
      });

      // Convert type to display name
      const typeDisplayNames = {
        single: "Single Location",
        bulk: "Bulk Update",
        recommended: "Recommendation",
      };

      return typeDisplayNames[maxType] || "None";
    },

    mostCommonUpdateCount() {
      if (!this.updateChanges.length) return 0;

      const typeCounts = {
        single: 0,
        bulk: 0,
        recommended: 0,
      };

      this.updateChanges.forEach((entry) => {
        if (entry.type === "single") typeCounts.single++;
        else if (entry.type === "recommended") typeCounts.recommended++;
        else typeCounts.bulk++; // 'multiple' or 'bulk'
      });

      return Math.max(
        typeCounts.single,
        typeCounts.bulk,
        typeCounts.recommended
      );
    },
  },
  methods: {
    isImageUrl(val = "") {
      return (
        typeof val === "string" &&
        /^https?:\/\/.+\.(jpeg|jpg|png|webp|gif|heic|heif)(\?.*)?$/i.test(val)
      );
    },

    /* optional: ask Cloudinary / GBP for a 150-px wide thumbnail */
    thumbUrl(url = "") {
      if (!this.isImageUrl(url)) return url;

      // Cloudinary → insert a transformation segment right after /upload/
      if (/res\.cloudinary\.com/.test(url)) {
        return url.replace("/upload/", "/upload/c_scale,w_150/");
      }

      // Google Business images → append '=s150' if not already present
      if (/googleusercontent\.com/.test(url) && !/[?&=]s\d+/.test(url)) {
        return `${url}=s150`;
      }
      return url;
    },
    normaliseResult(result, parentEntry) {
      // 1. field already present (bulk / multi)
      if (result.field)
        return {
          ...result,
          fxield: this.formatFieldName(result.field),
        };

      // 2. updatedAttributes object → treat as "Attributes"
      if (result.updatedAttributes) {
        return {
          ...result,
          field: "Location Attributes",
        };
      }

      // 3. fallback: derive from first change block (single-edit)
      if (Array.isArray(parentEntry.changes) && parentEntry.changes.length) {
        return {
          ...result,
          field: parentEntry.changes[0].field || "Unknown",
        };
      }

      return { ...result, field: "Unknown" };
    },
    async initializeDataLoading() {
      try {
        this.isLoading = true;

        // Validate that we have the required IDs
        if (!this.currentUID || !this.WID) {
          console.error("Missing required IDs for data loading", {
            currentUID: this.currentUID,
            WID: this.WID,
          });
          this.hasError = true;
          this.errorMessage =
            "Authentication information missing. Please try refreshing the page.";
          this.isLoading = false;
          return;
        }

        // Get reference to the collection
        const collectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-update-history"
        );

        // Create the initial query
        const initialQuery = query(
          collectionRef,
          orderBy("requested_at", "desc"),
          limit(this.batchSize)
        );

        // Execute the query
        const snapshot = await getDocs(initialQuery);

        if (snapshot.empty) {
          // No data available - this is a valid state
          this.isLoading = false;
          return;
        }

        // Process the documents
        const changes = snapshot.docs.map((doc) => {
          const data = doc.data();
          let results = data.updateResults || [];

          if (
            data.locationId &&
            data.updateResults &&
            data.updateResults[data.locationId]
          ) {
            results = data.updateResults[data.locationId];
          }

          return {
            id: doc.id,
            ...data,
            updateResults: results,
            showChanges: false,
            showAllLocations: false,
            recommendation: null,
          };
        });

        // Update state
        this.updateChanges = changes;
        this.lastDoc = snapshot.docs[snapshot.docs.length - 1] || null;
      } catch (error) {
        console.error("Error initializing history data:", error);
        this.hasError = true;
        this.errorMessage = error.message || "Failed to load update history";
      } finally {
        this.isLoading = false;
      }
    },

    toggleTypeFilter(type) {
      if (this.selectedTypes.includes(type)) {
        this.selectedTypes = this.selectedTypes.filter((t) => t !== type);
      } else {
        this.selectedTypes.push(type);
      }
    },

    toggleStatusFilter(status) {
      if (this.selectedStatuses.includes(status)) {
        this.selectedStatuses = this.selectedStatuses.filter(
          (s) => s !== status
        );
      } else {
        this.selectedStatuses.push(status);
      }
    },

    resetFilters() {
      this.searchQuery = "";
      this.selectedTypes = [];
      this.selectedStatuses = [];
    },

    getSuccessPercentage(entry) {
      if (!entry) return 0;
      const successCount = this.getSuccessCount(entry);
      const failedCount = this.getFailedCount(entry);
      const total = successCount + failedCount;
      return total ? (successCount / total) * 100 : 0;
    },

    getFailedPercentage(entry) {
      if (!entry) return 0;
      const successCount = this.getSuccessCount(entry);
      const failedCount = this.getFailedCount(entry);
      const total = successCount + failedCount;
      return total ? (failedCount / total) * 100 : 0;
    },

    handleGoBack() {
      this.$emit("goBack");
    },

    async toggleDetails(entry) {
      if (!entry.changes?.length && entry.type === "recommended") {
        try {
          const docRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "gmb-recommendations",
            entry.recommendation_id
          );

          const docSnapshot = await getDoc(docRef);
          if (docSnapshot.exists()) {
            entry.recommendation = docSnapshot.data();
            console.log("Recommendation data loaded:", entry.recommendation);
          } else {
            console.error(
              "Recommendation document not found:",
              entry.recommendation_id
            );
          }
        } catch (error) {
          console.error("Error loading recommendation details:", error);
        }
        if (
          entry.recommendation?.field === "primary_category" &&
          entry.recommendation.location_recommendations?.length
        ) {
          // Create a lookup map once instead of repeatedly searching the array
          const categoryMap = {};
          this.categories.forEach((category) => {
            if (category.name) {
              categoryMap[category.name] = category.displayName;
            }
          });

          // Now efficiently process each location
          entry.recommendation.location_recommendations.forEach((location) => {
            // Update current value using the map
            if (location.current_value) {
              location.current_value =
                categoryMap[location.current_value] || location.current_value;
            }

            // Update recommended value using the map
            if (location.recommended_values?.recommended_value) {
              location.recommended_values.recommended_value =
                categoryMap[location.recommended_values.recommended_value] ||
                location.recommended_values.recommended_value;
            }
          });
        }
      }

      entry.showChanges = !entry.showChanges;
      this.showAllSubChanges = false;
      this.recommendationSearchQuery = "";
    },

    processChanges(data, field) {},

    reApplyChanges(change) {
      console.log(change, "this is the change");
    },

    formatFirestoreDate(timestamp) {
      if (!timestamp) return "N/A";
      const date = new Date(
        timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000
      );

      // For dates today, show just the time
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return `Today at ${date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      }

      // For dates in the last week, show the day name
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      if (date > weekAgo) {
        return `${date.toLocaleDateString([], {
          weekday: "long",
        })} at ${date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      }

      // For older dates, show the full date
      return date.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatChangeValue(field, value) {
      if (!value) return "None";

      // Handle special formatting for complex fields
      switch (field) {
        case "Business Hours":
          return this.formatBusinessHours(value);
        case "Additional Categories":
        case "Primary Category":
          return this.formatCategories(value);
        default:
          return typeof value === "object"
            ? JSON.stringify(value, null, 2)
            : value;
      }
    },

    formatBusinessHours(hours) {
      if (!hours?.periods) return "No hours set";
      return hours.periods
        .map(
          (p) =>
            `${p.openDay}: ${this.formatTime(p.openTime)} - ${this.formatTime(
              p.closeTime
            )}`
        )
        .join("\n");
    },

    formatTime(time) {
      return new Date(0, 0, 0, time.hours, time.minutes).toLocaleTimeString(
        [],
        {
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    },

    formatCategories(cats) {
      if (Array.isArray(cats)) return cats.join(", ");
      if (typeof cats === "string") return cats;
      return "No categories";
    },

    formatFieldName(field) {
      if (!field) return "Unknown";
      const key = field.toLowerCase();
      const map = {
        websiteuri: "Website URL",
        phonenumbers: "Phone Number",
        primary_category: "Primary Category",
        "categories.primarycategory": "Primary Category",
        additional_categories: "Additional Categories",
        "categories.additionalcategories": "Additional Categories",
        regularhours: "Business Hours",
        profile: "Business Description",
        title: "Business Name",
        attributes: "Location Attributes",
        "media.photos": "Photo",
        "media.logo": "Logo",
        "media.coverphoto": "Cover Photo",
        openInfo: "Open Info",
      };

      if (map[key]) return map[key];

      // generic camel/snake → Title-case fallback
      return key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^\w/, (c) => c.toUpperCase());
    },

    filteredLocationRecommendations(recommendation) {
      if (!recommendation || !recommendation.location_recommendations) {
        return [];
      }

      return recommendation.location_recommendations.map((loc) => ({
        ...loc,
        recommended_value: loc.recommended_values.recommended_value,
      }));
    },

    async fetchChangeHistory($state) {
      if (!$state) return;

      try {
        // If we already have data and no lastDoc, we've reached the end
        if (this.updateChanges.length > 0 && !this.lastDoc) {
          $state.complete();
          return;
        }

        // Build the query more efficiently
        const collectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-update-history"
        );

        const queryConstraints = [
          orderBy("requested_at", "desc"),
          limit(this.batchSize),
        ];

        if (this.lastDoc) {
          queryConstraints.push(startAfter(this.lastDoc));
        }

        const gmbHistoryQuery = query(collectionRef, ...queryConstraints);
        const gmbHistoryDocs = await getDocs(gmbHistoryQuery);

        if (gmbHistoryDocs.empty) {
          $state.complete();
          return;
        }

        // Process the documents more efficiently
        const changes = gmbHistoryDocs.docs.map((doc) => {
          const data = doc.data();
          // More efficiently handle updateResults
          let results = data.updateResults || [];

          // Handle the special case for locationId
          if (
            data.locationId &&
            data.updateResults &&
            data.updateResults[data.locationId]
          ) {
            results = data.updateResults[data.locationId];
          }

          return {
            id: doc.id,
            ...data,
            updateResults: results,
            showChanges: false,
            showAllLocations: false,
            // Add a placeholder for recommendation data
            recommendation: null,
          };
        });

        // Update component state
        this.updateChanges.push(...changes);
        this.lastDoc =
          gmbHistoryDocs.docs[gmbHistoryDocs.docs.length - 1] || null;

        // Handle pagination state
        if (changes.length < this.batchSize) {
          $state.complete();
        } else {
          $state.loaded();
        }
      } catch (error) {
        console.error("Error fetching change history:", error);
        $state.error();
        this.hasError = true;
        this.errorMessage = error.message || "Failed to load more updates";
      }
    },

    getSuccessCount(entry) {
      if (!entry) return 0;
      if (entry?.successCount) return entry?.successCount;
      if (!entry?.updateResults) return 0;
      return entry.updateResults.filter((result) => result.status === "success")
        .length;
    },

    getFailedCount(entry) {
      if (!entry) return 0;
      if (entry?.failedCount) return entry?.failedCount;
      if (!entry?.updateResults) return 0;
      return entry.updateResults.filter((result) => result.status === "failed")
        .length;
    },

    async showUpdateResultsDialog(entry) {
      if (!entry?.updateResults?.length && entry.type == "recommended") {
        const docRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-recommendations-updates",
          entry.recommendation_id
        );
        const docSnapshot = await getDoc(docRef);
        entry.updateResults = docSnapshot.data().updateResults;
      }
      // ── NEW: normalise each item
      this.currentUpdateResults = (entry.updateResults || []).map((r) =>
        this.normaliseResult(r, entry)
      );

      this.currentHistory = entry;
      this.resultSearchQuery = "";
      this.showUpdateResults = true;
    },

    isMultiLocation(entry) {
      return (
        (entry.type === "multiple" || entry.type === "bulk") &&
        this.getAffectedLocationsCount(entry) > 1
      );
    },

    getAffectedLocationsCount(entry) {
      if (
        entry.location_recommendations &&
        Array.isArray(entry.location_recommendations)
      ) {
        return entry.location_recommendations.length;
      }

      if (entry.locations && Array.isArray(entry.locations)) {
        return entry.locations.length;
      }

      return 0;
    },

    toggleLocationsList(entry) {
      // Use Vue.set to ensure reactivity
      if (!entry.hasOwnProperty("showAllLocations")) {
        this.$set(entry, "showAllLocations", true);
      } else {
        entry.showAllLocations = !entry.showAllLocations;
      }
    },

    getVisibleLocations(entry) {
      let locations = [];

      if (
        entry.location_recommendations &&
        Array.isArray(entry.location_recommendations)
      ) {
        locations = entry.location_recommendations;
      } else if (entry.locations && Array.isArray(entry.locations)) {
        locations = entry.locations;
      }

      // If showAllLocations is true, show all locations, otherwise show only first 3
      return entry.showAllLocations ? locations : locations.slice(0, 3);
    },

    getLocationName(location) {
      if (location.name) {
        return location.name;
      }

      if (location.title) {
        return location.title;
      }

      if (location.location_id) {
        // Try to find a matching location in the locations list
        const foundLocation = this.locations?.find(
          (loc) =>
            loc.id === location.location_id ||
            loc.name === location.location_id ||
            (loc.gmbProfile && loc.gmbProfile.name === location.location_id)
        );

        if (foundLocation) {
          return foundLocation.title || foundLocation.name || "Location";
        }
      }

      return "Unnamed Location";
    },

    async fetchLocations() {
      try {
        // Validate IDs
        if (!this.currentUID || !this.WID) {
          console.warn("Missing IDs for location fetch");
          return;
        }

        // Query the workspace locations
        const locationsRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces"
        );

        const q = query(
          locationsRef,
          where("storeWorkspace", "==", true),
          where("admin", "==", this.WID)
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          this.locations = [];
          return;
        }

        // Process locations
        this.locations = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure we have a name reference for matching
            name: data.gmbProfile?.name || doc.id,
            title:
              data.title ||
              data.gmbProfile?.title ||
              data.name ||
              "Unnamed Location",
          };
        });
      } catch (error) {
        console.error("Error fetching locations:", error);
        this.locations = [];
      }
    },

    getErrorMessage(codeOrText) {
      // If the backend already stored a full sentence just return it.
      return (
        GMB_ERROR_MESSAGES[codeOrText] ||
        "An unexpected error occurred. Please try again."
      );
    },

    getStatusColor(status) {
      const statusColors = {
        queued: "orange",
        verified: "blue",
        rejected: "pink",
        success: "green",
        failed: "red",
      };
      return statusColors[status] || "gray";
    },

    getStatusCount(entry, status) {
      if (!entry || !entry.updateResults) return 0;
      return entry.updateResults.filter((result) => result.status === status)
        .length;
    },

    getStatusPercentage(entry, status) {
      if (!entry || !entry.updateResults) return 0;
      const total = entry.updateResults.length;
      const count = this.getStatusCount(entry, status);
      return total ? (count / total) * 100 : 0;
    },
  },
};
</script>

<style scoped>
.whitespace-pre-line {
  white-space: pre-line;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.v-data-table ::v-deep th {
  font-weight: 600 !important;
  color: #374151 !important;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f3f4f6 !important;
}

.stat-card {
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Fix for icon display */
.v-icon {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  line-height: 1;
  aspect-ratio: 1;
}

/* Timeline dot container */
.timeline-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

/* Material Design Icons fixes */
.mdi {
  font-size: 24px;
  width: 24px;
  height: 24px;
  line-height: 1;
  display: inline-block;
}

.mdi-map-marker {
  font-size: 24px;
}

.mdi-file-multiple {
  font-size: 22px;
}

.mdi-lightbulb-on {
  font-size: 22px;
}

/* Filter chip icons */
.filter-chip {
  min-width: 90px;
  font-weight: 500;
}

/* Selected chip styles */
.selected-chip {
  color: #1976d2 !important; /* blue */
}
.selected-chip[color="blue"] {
  color: #1976d2 !important;
}
.selected-chip[color="purple darken-2"] {
  color: #7b1fa2 !important;
}
.selected-chip[color="green"] {
  color: #2e7d32 !important;
}

/* Fix for selected filter chips text visibility */
.filter-chip :deep(.v-chip__content) {
  color: inherit;
}

.filter-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.filter-icon-wrapper .mdi {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

/* Material Design Icons in filter chips */
.filter-chip .mdi-map-marker {
  font-size: 16px;
}

.filter-chip .mdi-file-multiple {
  font-size: 14px;
}

.filter-chip .mdi-lightbulb-on {
  font-size: 14px;
}

/* Locations list styles */
.locations-list {
  max-height: 300px;
  overflow-y: auto;
}

.locations-list > div {
  transition: all 0.2s ease;
}

.locations-list > div:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Add icon for the locations list */
.mdi-map-marker-multiple {
  font-size: 18px;
  color: #4f46e5;
}
</style>
